import { createHOC } from 'vue-hoc';
import keyBy from 'lodash/keyBy';
import gql from 'graphql-tag';
import get from 'lodash/get';

import withUser from '~lib/common/hoc/withUser';
import casesService from '~lib/common/providers/casesService';
import fetchOrgData from '~lib/common/utils/fetchOrgData';

import GradingReports from './GradingReports';
import gradingClient from '../../../providers/gradingClient';
import assignmentQueries from '~lib/common/queries/Assignment';
import attemptQueries from '../../../graphql/queries/Attempt';

import getAssignmentUserIds from '../../../utils/getAssignmentUserIds';

const options = {
  name: 'GradingReportsContainer',
  props: {
    id: {
      type: String,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    idToken: {
      type: String,
    },
  },
  data() {
    return {
      state: {
        loading: false,
        searching: false,
        reports: [],
        allReports: [],
        error: null,
        caseConf: null,
        assignment: null,
        selectedReport: null,
        reportUser: null,
        paged: this.$router.currentRoute.params.id && !this.$router.currentRoute.query.assignmentId,
        page: 1,
        pageSize: 25,
        userCount: 0,
        previousCursor: null,
        nextCursor: null,
      },
    };
  },
  computed: {
    reportsByUserId() {
      return keyBy(this.state.reports, 'user.user_id');
    },
    numPages() {
      return Math.ceil(this.state.userCount / this.state.pageSize);
    },
  },
  async created() {
    await this.fetchOrgGroupsAndUsers();
    if (this.state.paged) {
      await this.fetchReportSummariesPaged(true);
    } else {
      await this.fetchReportSummaries();
    }
    await this.fetchCommon();
  },
  methods: {
    async fetchOrgGroupsAndUsers() {
      try {
        this.state.loading = true;
        this.state.orgData = await fetchOrgData(this.user);
        this.state.usersById = keyBy(this.state.orgData.users, 'user_id');
      } catch (e) {
        console.error(e);
        this.state.error = e;
      }
    },
    async fetchCommon() {
      try {
        this.state.loading = true;
        const caseId = this.$router.currentRoute.params.id;
        const userId = this.$router.currentRoute.query.userId;
        const { assignmentId } = this.$router.currentRoute.query;

        const [assignment, attempts] = await Promise.all([
          assignmentId ? this.getAssignment(assignmentId) : Promise.resolve(null),
          assignmentId ? this.getAttempts(assignmentId) : Promise.resolve([])
        ]);
        this.state.caseConf = {
          id: caseId,
          name: get(assignment, 'caseName') || this.$router.currentRoute.query.caseName,
        };
        this.state.assignment = assignment;
        this.state.attempts = attempts;
        if (userId) {
          this.state.reportUser = this.state.usersById[userId];
        }
        if (assignment) {
          const assignmentUserIds = await getAssignmentUserIds(
            assignment,
            this.state.orgData.groups
          );
          // Create stub reports for users in the assignment that don't have grading reports yet
          const userIdsWithoutReports = assignmentUserIds.filter(userId => {
            const user = this.state.usersById[userId];
            if (!user) {
              return false;
            }
            // Instructors/admins might be part of groups but we don't want to display stub reports for them
            if (user.app_metadata.role !== 'STUDENT') {
              return false;
            }
            return !this.reportsByUserId[userId];
          });
          this.state.reports = this.state.reports.concat(
            userIdsWithoutReports.map(userId => ({
              isStub: true,
              user: this.state.usersById[userId],
              userId,
            }))
          );
          // Set the throughGroup property on the reports that are assigned through groups
          if (assignment.students.groups) {
            assignment.students.groups.forEach(groupId => {
              const group = this.state.orgData.groups.find(grp => grp._id === groupId);
              if (group && group.members) {
                group.members.forEach(memberId => {
                  const memberReport = this.reportsByUserId[memberId];
                  if (!memberReport || !memberReport.user) {
                    return;
                  }
                  memberReport.throughGroup = group;
                });
              }
            });
          }
        }
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async fetchReportSummaries() {
      try {
        const caseId = this.$router.currentRoute.params.id;
        const userId = this.$router.currentRoute.query.userId;
        const { assignmentId } = this.$router.currentRoute.query;

        const reportsResponse = await gradingClient.get('/reports/summaries', {
            filter: {
              caseId,
              userId,
              assignmentId,
            },
          })
        this.state.reports = reportsResponse.data.gradingreports;
        this.state.reports = this.state.reports.map(report => ({
          ...report,
          user: this.state.usersById[report.userId],
        }));
      } catch (e) {
        console.error(e);
        this.state.error = e;
      }
    },
    async fetchReportSummariesPaged(initial, next, previous, last) {
      try {
        this.state.loading = true;
        const caseId = this.$router.currentRoute.params.id;

        const reportQuery = {
          caseId,
          limit: last ? this.state.userCount % this.state.pageSize : this.state.pageSize,
          fetchCount: initial ? true : undefined,
          after: next ? this.state.nextCursor : undefined,
          before: previous ? this.state.previousCursor : undefined,
          last: last ? true : undefined,
        };
        const reportsResponse = await gradingClient.get('/reports/summaries-paged', reportQuery);
        this.state.reports = reportsResponse.data.gradingreports;
        this.state.previousCursor = reportsResponse.data.previousCursor;
        this.state.nextCursor = reportsResponse.data.nextCursor;
        if (initial) {
          this.state.userCount = reportsResponse.data.userCount;
          this.state.page = 1;
        }
        this.state.reports = this.state.reports.map(report => ({
          ...report,
          user: this.state.usersById[report.userId],
        }));
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }

    },
    async getAssignment(assignmentId) {
      try {
        const response = await casesService.query({
          query: gql(assignmentQueries.fetchById),
          variables: {
            id: assignmentId,
          },
          fetchPolicy: 'network-only',
        });
        return response.data.assignments[0];
      } catch (e) {
        console.error(e);
        this.state.error = e;
      }
    },
    async getAttempts(assignmentId) {
      try {
        const response = await casesService.query({
          query: gql(attemptQueries.fetchByAssignmentId),
          variables: {
            assignmentId,
          },
          fetchPolicy: 'network-only',
        });
        return response.data.attempts;
      } catch (e) {
        console.error(e);
        this.state.error = e;
      }
    },
    async addAssignmentAttempts(user) {
      try {
        this.state.loading = true;
        const { assignment } = this.state;
        const updatedAssignment = {
          ...assignment,
          additionalAttempts: (assignment.additionalAttempts || []).concat({
            principalType: 'USER',
            principalId: user.user_id,
            count: 1,
          })
        }
        await casesService.mutate({
          mutation: gql(assignmentQueries.save),
          variables: {
            value: updatedAssignment,
          },
          fetchPolicy: 'no-cache',
        });
        this.state.assignment = updatedAssignment;
      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }
    },
    async fetchSingleReport(report) {
      if (!report) {
        this.state.selectedReport = null;
        return;
      }
      // The component stores a list of grades that can be selected in a dropdown
      // in a history property.  The first grade in the history is the most recent one,
      // and the one passed to this method.
      // Fetch the full reports for the history list and set the parent report to the
      // first in the history.
      try {
        this.state.loading = true;
        const reportsResponse = await Promise.all(
          report.history.map(historyItem =>
            gradingClient.get('/reports', {
              filter: {
                _id: historyItem._id,
              },
            })
          )
        );

        this.state.selectedReport = reportsResponse[0].data.gradingreports[0];
        this.state.selectedReport.user = report.user;
        this.state.selectedReport.history = reportsResponse.map(
          res => res.data.gradingreports[0]
        );
        this.state.selectedReport.history = this.state.selectedReport.history.map(report => ({
          ...report,
          user: this.state.usersById[report.userId],
        }));
        this.state.caseConf = {
          id: this.state.selectedReport.caseId,
          name: this.state.selectedReport.caseName
        };

      } catch (e) {
        console.error(e);
        this.state.error = e;
      } finally {
        this.state.loading = false;
      }

    },
  },
};

const renderWith = {
  props: {
    loading() {
      return this.state.loading;
    },
    searching() {
      return this.state.searching;
    },
    reports() {
      return this.state.reports;
    },
    error() {
      return this.state.error;
    },
    caseConf() {
      return this.state.caseConf;
    },
    assignment() {
      return this.state.assignment;
    },
    user() {
      return this.user;
    },
    reportUser() {
      return this.state.reportUser;
    },
    attempts() {
      return this.state.attempts;
    },
    groupByCase() {
      return !this.$router.currentRoute.params.id;
    },
    groupByUser() {
      return !this.$router.currentRoute.query.userId;
    },
    selectedReport() {
      return this.state.selectedReport;
    },
    paged() {
      return this.state.paged;
    },
    paginationText() {
      if (this.state.userCount === 0) {
        return '';
      }
      return `${this.state.page} of ${this.numPages}`;
    },
  },
  listeners: {
    addAssignmentAttempts(user) {
      this.addAssignmentAttempts(user);
    },
    selectReport(report) {
      this.fetchSingleReport(report);
    },
    firstPage() {
      if (this.state.page > 1) {
        this.state.page = 1;
        this.fetchReportSummariesPaged(false, false, false);
      }
    },
    previousPage() {
      if (this.state.page > 1) {
        this.state.page -= 1;
        this.fetchReportSummariesPaged(false, false, true);
      }
    },
    nextPage() {
      if (this.state.page < this.numPages) {
        this.state.page += 1;
        this.fetchReportSummariesPaged(false, true, false);
      }
    },
    lastPage() {
      if (this.state.page < this.numPages) {
        this.state.page = this.numPages;
        this.fetchReportSummariesPaged(false, false, false, true);
      }
    },
  },
};

const GradingReportsContainer = createHOC(GradingReports, options, renderWith);
export default withUser()(GradingReportsContainer);
